/**
 * Package Imports
 */
import { createApp } from 'vue'
import { createI18n } from 'vue-i18n'
import { createStore } from 'vuex'
import Bugsnag from '@bugsnag/js'
import BugsnagPluginVue from '@bugsnag/plugin-vue'
import BugsnagPerformance from '@bugsnag/browser-performance'
import mitt from 'mitt'
import VTooltip from 'v-tooltip'
import VueTippy from 'vue-tippy'
import { vMaska } from 'maska'
import 'tippy.js/dist/svg-arrow.css'
import { VueReCaptcha } from 'vue-recaptcha-v3'
import Vue3Lottie from 'vue3-lottie'
import VueScreen from 'vue-screen'
import { createMetaManager } from 'vue-meta'

/**
 * User Imports
 */
import globalModule from '@config/vuex/globalModule.js'
import statusModule from '@js/configs/statusModule.js'
import resultModule from '@config/vuex/resultModule.js'
import applyModule from '@config/vuex/applyModule.js'
import ratesTableModule from '@config/vuex/ratesTableModule.js'
import initRouter from '@js/utilities/initRouter'
import fetchFullTranslations from '@js/utilities/translations'
import translations from '@config/translations.js'
import loadComponents from '@js/utilities/loadComponents'
import { forceRelativeUrl, removeAppUrl, url } from '@js/utilities/url.js'
import { titleCase } from '@js/utilities/language.js'
import { themeSetting } from '@js/utilities/themeSettings.js'
import { trackEvent } from '@js/utilities/tracking.js'
import { asset, autoFocus, checkMobile, excludeTerritories, includeTerritories } from '@js/utilities/globalCustom.js'
import { isEmptyValue, extractVParams } from '@js/utilities/general.js'
import { defineValidationRules } from '@js/utilities/validationRules'

/**
 * Initialize Bugsnag
 */
const app = createApp({})

if (window.BUGSNAG_VUE_API_KEY) {
    Bugsnag.start({
        apiKey: window.BUGSNAG_VUE_API_KEY,
        releaseStage: window.APP_ENV,
        plugins: [new BugsnagPluginVue()],
        enabledBreadcrumbTypes: ['error', 'log', 'request', 'user'],
        onError: function (event) {
            // Ignore specific errors or files
            const ignoreMethods = ['nrWrapper']
            const ignoreFiles = ['js-agent.newrelic.com']
            const ignoreErrors = ['__gCrWeb']

            if (event.errors) {
                for (let i = 0; i < event.errors.length; i++) {
                    const error = event.errors[i]

                    for (const ignoreError of ignoreErrors) {
                        if (error?.errorMessage?.includes(ignoreError)) {
                            return false
                        }
                    }

                    if (error.stacktrace) {
                        const stacks = error.stacktrace

                        if (stacks.length) {
                            const stack = stacks[0]

                            for (const ignoreMethod of ignoreMethods) {
                                if (stack?.method?.includes(ignoreMethod)) {
                                    return false
                                }
                            }

                            for (const ignoreFile of ignoreFiles) {
                                if (stack?.file?.includes(ignoreFile)) {
                                    return false
                                }
                            }
                        }
                    }
                }
            }

            return true
        },
    })

    BugsnagPerformance.start({ apiKey: window.BUGSNAG_VUE_API_KEY })

    const bugsnagVue = Bugsnag.getPlugin('vue')
    app.use(bugsnagVue)
}
/**
 * Init Vue
 */
const territory = process.env.TERRITORY

/**
 * Emitter
 */
const emitter = mitt()
app.config.globalProperties.emitter = emitter

/**
 * Translations
 */
window.translationsLoaded = false

const locale = translations.locale(territory)
const i18n = createI18n({
    locale: locale,
    messages: translations.messages,
    warnHtmlInMessage: 'off', // disable of the Detected HTML in message
})

/**
 * Vuex
 */
const store = createStore({
    modules: {
        apply: applyModule,
        global: globalModule,
        result: resultModule,
        status: statusModule,
        ratesTable: ratesTableModule,
    },
})

store.state.apply.$t = i18n.global.t
store.state.global.$t = i18n.global.t
store.state.result.$t = i18n.global.t
store.state.ratesTable.$t = i18n.global.t

const translationsPromise = fetchFullTranslations(i18n, store)

/**
 * Axios and CSRF Token
 */
window.axios = require('axios')
window.axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest'

const token = document.head.querySelector('meta[name="csrf-token"]')

if (token) {
    window.axios.defaults.headers.common['X-CSRF-TOKEN'] = token.content
} else {
    console.error('CSRF token not found: https://laravel.com/docs/csrf#csrf-x-csrf-token')
}

/**
 * Router (and global data)
 */
const router = initRouter(store, i18n, translationsPromise, emitter)

/**
 * Vee Validate
 */
defineValidationRules()

/**
 * Mixins
 */
app.mixin({
    methods: {
        url: url,
        removeAppUrl: removeAppUrl,
        forceRelativeUrl: forceRelativeUrl,
        titleCase: titleCase,
        themeSetting: themeSetting,
        trackEvent: trackEvent,
        includeTerritories: includeTerritories,
        excludeTerritories: excludeTerritories,
        autoFocus: autoFocus,
        asset: asset,
        isEmptyValue: isEmptyValue,
        extractVParams: extractVParams,
        checkMobile: checkMobile,
    },
})

/**
 * Checks Device Touch Support
 */
window.isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0

/**
 * Import components
 */
loadComponents(app, store)

/**
 * Import directives
 */
const directives = require.context('./directives/', true, /\.js/i)
directives.keys().map(key => {
    app.directive(key.split('/').pop().split('.')[0], directives(key).default)
})

app.directive('maska', vMaska)

/**
 * Plugins
 */
app.use(i18n)
app.use(store)
app.use(router)
app.use(VTooltip)
app.use(VueScreen, 'bootstrap')
app.use(Vue3Lottie)

app.use(createMetaManager())
app.use(VueTippy, {
    directive: 'tippy',
})

if (process.env.RECAPTCHA_SITE_KEY) {
    app.use(VueReCaptcha, {
        siteKey: process.env.RECAPTCHA_SITE_KEY,
        loaderOptions: {
            autoHideBadge: true,
        },
    })
}

;(async function () {
    /**
     * Mount
     */
    app.mount('#app')
})()
